import Calculator from './Calculator.js';

/**
 * PotCalculator - Calculator for round flowerpots
 *
 * Formula for pot (doniczka):
 * - d = diameter (średnica) in cm
 * - h = height (wysokość) in cm
 * - drainageRatio = drainage percentage (default 15%)
 *
 * With drainage:
 * - Substrate volume = (π*(d/2)²*h*(1-drainageRatio/100)) / 1000
 * - Drainage volume = (π*(d/2)²*h*(drainageRatio/100)) / 1000
 *
 * Without drainage:
 * - Substrate volume = (π*(d/2)²*h) / 1000
 * - Drainage volume = 0
 *
 * @class PotCalculator
 * @extends Calculator
 */
class PotCalculator extends Calculator {
    /**
     * @param {Object} dimensions
     * @param {number} dimensions.d - Diameter in cm
     * @param {number} dimensions.h - Height in cm
     * @param {number} [dimensions.drainageRatio=15] - Drainage ratio percentage
     * @param {boolean} [withDrainage=true] - Whether to include drainage
     */
    constructor(dimensions, withDrainage = true) {
        super(dimensions);
        const { d = 0, h = 0, drainageRatio = 15 } = dimensions;

        this.d = parseFloat(d) || 0;
        this.h = parseFloat(h) || 0;
        this.withDrainage = withDrainage;
        this.drainageRatio = drainageRatio;
        this.PI = 3.14;
    }

    /**
     * Calculate substrate volume for round pot
     *
     * @returns {number} Substrate volume in liters
     */
    calculateSubstrateVolume() {
        const radius = this.d / 2;
        const baseArea = this.PI * (radius * radius);
        const CM3_TO_LITERS = 1000;

        if (this.withDrainage) {
            const effectiveHeight = this.h * (1 - (this.drainageRatio / 100));
            return (baseArea * effectiveHeight) / CM3_TO_LITERS;
        }

        return (baseArea * this.h) / CM3_TO_LITERS;
    }

    /**
     * Calculate drainage volume for round pot
     *
     * @returns {number} Drainage volume in liters
     */
    calculateDrainageVolume() {
        if (!this.withDrainage) {
            return 0;
        }

        const radius = this.d / 2;
        const baseArea = this.PI * (radius * radius);
        const drainageHeight = this.h * (this.drainageRatio / 100);
        const CM3_TO_LITERS = 1000;

        return (baseArea * drainageHeight) / CM3_TO_LITERS;
    }

    /**
     * Set dimensions
     *
     * @param {Object} dimensions
     */
    setDimensions(dimensions) {
        Object.assign(this.dimensions, dimensions);
        if (dimensions.d !== undefined) this.d = parseFloat(dimensions.d) || 0;
        if (dimensions.h !== undefined) this.h = parseFloat(dimensions.h) || 0;
        if (dimensions.withDrainage !== undefined) this.withDrainage = dimensions.withDrainage;
        if (dimensions.drainageRatio !== undefined) this.drainageRatio = parseFloat(dimensions.drainageRatio) || 15;
    }

    /**
     * Validate dimensions
     *
     * @returns {Object} Validation result
     */
    validate() {
        const errors = [];

        if (this.d <= 0) errors.push({ field: 'd', type: 'required' });
        if (this.h <= 0) errors.push({ field: 'h', type: 'required' });

        return {
            isValid: errors.length === 0,
            errors
        };
    }
}

export default PotCalculator;